import { Alert, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import React, { FunctionComponent, useMemo } from 'react'

import { PROPERTY_TYPES } from '../../../common/drmTypes'
import { numberSorter, stringSorter } from '../../utils/generalTableHelpers'
import { sparsePagination } from '../../utils/layoutConst'
import { propertyListAreaDetailFilterOptions as _options } from '../../utils/propertyListAreaTableHelpers'
import { useColumnFilters } from '../../utils/useColumnFilters'
import { useColumnOptionFilter } from '../../utils/useColumnOptionFilter'
import type { PropertyListAreaDetailRow as Item } from '../../utils/useFetchPropertyListDetailArea'
import { useFetchPropertyListAreaDetail } from '../../utils/useFetchPropertyListDetailArea'
import { useTableLocale } from '../../utils/useTableLocale'
import { Money } from '../Money/Money'
import { PropertyListDetailPropertyDetailLink } from '../PropertyListDetailPropertyDetailLink/PropertyListDetailPropertyDetailLink'

export interface PropertyListAreaDetailTableProps {
  whereClause: string
}

const scrollX = { x: 'max-content' }
const tableId = 'property-list-detail-area'

export const PropertyListAreaDetailTable: FunctionComponent<PropertyListAreaDetailTableProps> =
  ({ whereClause }) => {
    const {
      data: dataSource,
      loading,
      error,
    } = useFetchPropertyListAreaDetail(whereClause)

    const locale = useTableLocale(loading)

    const { rangeFilter, searchFilter } = useColumnFilters<Item>()

    const { getColumnOptionFilter: getColumnOptionFilterDruhPozemku } =
      useColumnOptionFilter<Item>(undefined, _options.druhPozemku)
    const { getColumnOptionFilter: getColumnOptionFilterZpusobVyuzitiPozemku } =
      useColumnOptionFilter<Item>(undefined, _options.zpusobVyuzitiPozemku)

    const columns = useMemo<ColumnsType<Item>>(
      () => [
        {
          title: 'Katastrální území',
          dataIndex: 'katastralniUzemiText',
          width: 180,
          sorter: stringSorter('katastralniUzemiText'),
          ...searchFilter('katastralniUzemiText'),
        },
        {
          title: 'Druh pozemku',
          dataIndex: 'druhPozemkuText',
          width: 180,
          sorter: stringSorter('druhPozemkuText'),
          ...getColumnOptionFilterDruhPozemku('druhpozemkukod'),
        },
        {
          title: 'Zp. využití',
          dataIndex: 'zpusobVyuzitiPozemkuText',
          width: 200,
          sorter: stringSorter('zpusobVyuzitiPozemkuText'),
          ...getColumnOptionFilterZpusobVyuzitiPozemku('zpusobyvyuzitipozemku'),
        },
        {
          title: 'Kmenové č.',
          dataIndex: 'kmenovecislo',
          width: 120,
          className: 'text-right',
          sorter: numberSorter('kmenovecislo'),
          ...rangeFilter('kmenovecislo'),
        },
        {
          title: 'Poddělení č',
          dataIndex: 'poddelenicisla',
          width: 120,
          className: 'text-right',
          sorter: numberSorter('poddelenicisla'),
          ...rangeFilter('poddelenicisla'),
        },
        {
          title: 'Výměra',
          dataIndex: 'vymeraparcely',
          className: 'text-right',
          width: 120,
          sorter: numberSorter('vymeraparcely'),
          ...rangeFilter('vymeraparcely'),
          render: (v: number | null, record) => (
            <Money
              amount={v || record['st_area(shape)']}
              decimalNumbers={0}
              symbol="m²"
              noData="---"
            />
          ),
        },
        {
          title: '',
          dataIndex: 'id',
          key: 'action',
          width: 160,
          className: 'text-right',
          render: (id: number, record) => (
            <PropertyListDetailPropertyDetailLink
              propertyCode={id.toString()}
              propertyType={PROPERTY_TYPES.AREA}
              buttonText="Zobrazit detail"
              areaRecord={record}
            />
          ),
        },
      ],
      [
        getColumnOptionFilterDruhPozemku,
        getColumnOptionFilterZpusobVyuzitiPozemku,
        rangeFilter,
        searchFilter,
      ]
    )

    if (error) return <Alert type="error" message={error} />

    return (
      <Table<Item>
        id={tableId}
        rowKey="key"
        size="small"
        loading={loading || !whereClause}
        locale={locale}
        dataSource={dataSource}
        columns={columns}
        scroll={scrollX}
        pagination={sparsePagination}
      />
    )
  }
